import {
  Mail, Building2, Shield, MapPin,
  Send, KeyRound, Ban, Power, Trash2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  FormField, FormInput, FormSelect 
} from '@/components/ui/form-modal';
import {
  USER_ROLE_LABELS,
  USER_STATUS_COLORS,
  USER_STATUS_LABELS,
  type User,
  type UserRole,
  type UpdateUserRequest,
} from '@/features/settings';

const LANGUAGE_OPTIONS = [
  {
    value: 'en',
    label: 'English' 
  },
  {
    value: 'es',
    label: 'Spanish' 
  },
  {
    value: 'fr',
    label: 'French' 
  },
  {
    value: 'de',
    label: 'German' 
  },
];

export function UserHeader({ user }: { readonly user: User }) {
  return (
    <div className="flex items-center gap-4">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary text-lg font-semibold">
        {user.firstName[0]}{user.lastName[0]}
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-semibold">{user.firstName} {user.lastName}</h3>
          <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${USER_STATUS_COLORS[user.status]}`}>
            {USER_STATUS_LABELS[user.status]}
          </span>
        </div>
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          <span className="flex items-center gap-1">
            <Mail className="h-3 w-3" /> {user.email}
          </span>
          <span className="flex items-center gap-1">
            <Shield className="h-3 w-3" /> {USER_ROLE_LABELS[user.role]}
          </span>
        </div>
      </div>
    </div>
  );
}

interface ProfileTabProps {
  readonly formData: UpdateUserRequest;
  readonly setFormData: (data: UpdateUserRequest) => void;
  readonly user: User;
}

export function ProfileTab({
  formData, setFormData, user 
}: ProfileTabProps) {
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <FormField label="First Name" required>
        <FormInput
          value={formData.firstName ?? ''}
          onChange={(e) => setFormData({
            ...formData,
            firstName: e.target.value 
          })}
          placeholder="John"
        />
      </FormField>
      <FormField label="Last Name" required>
        <FormInput
          value={formData.lastName ?? ''}
          onChange={(e) => setFormData({
            ...formData,
            lastName: e.target.value 
          })}
          placeholder="Doe"
        />
      </FormField>
      <FormField label="Email" className="md:col-span-2">
        <div className="flex items-center gap-2 rounded-md border bg-muted/50 px-3 py-2 text-sm text-muted-foreground">
          <Mail className="h-4 w-4" />
          {user.email}
          <span className="text-xs">(cannot be changed)</span>
        </div>
      </FormField>
      <FormField label="Phone">
        <FormInput
          type="tel"
          value={formData.phoneNumber ?? ''}
          onChange={(e) => setFormData({
            ...formData,
            phoneNumber: e.target.value 
          })}
          placeholder="+1 555 123 4567"
        />
      </FormField>
      <FormField label="Language">
        <FormSelect
          value={formData.preferredLanguage ?? 'en'}
          onChange={(e) => setFormData({
            ...formData,
            preferredLanguage: e.target.value 
          })}
          options={LANGUAGE_OPTIONS}
        />
      </FormField>
      <FormField label="Job Title">
        <FormInput
          value={formData.jobTitle ?? ''}
          onChange={(e) => setFormData({
            ...formData,
            jobTitle: e.target.value 
          })}
          placeholder="Executive Chef"
        />
      </FormField>
      <FormField label="Department">
        <FormInput
          value={formData.department ?? ''}
          onChange={(e) => setFormData({
            ...formData,
            department: e.target.value 
          })}
          placeholder="Culinary"
        />
      </FormField>
    </div>
  );
}

interface SelectOption {
  value: string;
  label: string;
}

interface AccessTabProps {
  readonly formData: UpdateUserRequest;
  readonly setFormData: (data: UpdateUserRequest) => void;
  readonly roleOptions: SelectOption[];
  readonly siteOptions: SelectOption[];
  readonly supplierOptions: SelectOption[];
  readonly isSupplier: boolean;
  readonly supplierName?: string;
}

export function AccessTab({
  formData,
  setFormData,
  roleOptions,
  siteOptions,
  supplierOptions,
  isSupplier,
  supplierName,
}: AccessTabProps) {
  return (
    <div className="space-y-4">
      <FormField label="Role" required>
        <FormSelect
          value={formData.role ?? ''}
          onChange={(e) => setFormData({
            ...formData,
            role: e.target.value as UserRole 
          })}
          options={roleOptions}
        />
      </FormField>

      {isSupplier ? (
        <FormField label="Supplier">
          {supplierName ? (
            <div className="flex items-center gap-2 rounded-md border bg-muted/50 px-3 py-2 text-sm">
              <Building2 className="h-4 w-4 text-muted-foreground" />
              {supplierName}
            </div>
          ) : (
            <FormSelect
              value={formData.supplierId ?? ''}
              onChange={(e) => setFormData({
                ...formData,
                supplierId: e.target.value 
              })}
              options={[{
                value: '',
                label: 'Select a supplier...' 
              }, ...supplierOptions]}
            />
          )}
        </FormField>
      ) : (
        <>
          <FormField label="Site Access">
            <FormSelect
              value={formData.siteAccess?.[0] ?? ''}
              onChange={(e) => setFormData({
                ...formData,
                siteAccess: e.target.value ? [e.target.value] : [],
              })}
              options={[{
                value: '',
                label: 'All sites (organization-wide)' 
              }, ...siteOptions]}
            />
            <p className="mt-1 text-xs text-muted-foreground">
              <MapPin className="inline h-3 w-3 mr-1" />
              {formData.siteAccess?.length 
                ? `Access to ${formData.siteAccess.length} site(s)`
                : 'Access to all sites'}
            </p>
          </FormField>

          <FormField label="Default Site">
            <FormSelect
              value={formData.defaultSiteId ?? ''}
              onChange={(e) => setFormData({
                ...formData,
                defaultSiteId: e.target.value 
              })}
              options={[{
                value: '',
                label: 'No default site' 
              }, ...siteOptions]}
            />
          </FormField>
        </>
      )}
    </div>
  );
}

interface ActionsTabProps {
  readonly user: User;
  readonly onAction: (action: 'disable' | 'enable' | 'reset-password' | 'resend-invitation' | 'delete') => Promise<void>;
  readonly isSubmitting: boolean;
}

export function ActionsTab({
  user, onAction, isSubmitting 
}: ActionsTabProps) {
  return (
    <div className="space-y-4">
      {user.status === 'pending' && (
        <ActionCard
          icon={<Send className="h-5 w-5 text-blue-500" />}
          title="Resend Invitation"
          description="Send a new invitation email with a fresh temporary password"
          buttonLabel="Resend"
          onClick={() => onAction('resend-invitation')}
          disabled={isSubmitting}
        />
      )}

      {user.status === 'active' && (
        <ActionCard
          icon={<KeyRound className="h-5 w-5 text-amber-500" />}
          title="Reset Password"
          description="Send a password reset email to the user"
          buttonLabel="Reset Password"
          onClick={() => onAction('reset-password')}
          disabled={isSubmitting}
        />
      )}

      {user.status === 'disabled' ? (
        <ActionCard
          icon={<Power className="h-5 w-5 text-green-500" />}
          title="Enable User"
          description="Re-enable this user's access to the system"
          buttonLabel="Enable"
          onClick={() => onAction('enable')}
          disabled={isSubmitting}
        />
      ) : (
        <ActionCard
          icon={<Ban className="h-5 w-5 text-orange-500" />}
          title="Disable User"
          description="Temporarily disable this user's access without deleting their account"
          buttonLabel="Disable"
          onClick={() => onAction('disable')}
          disabled={isSubmitting}
          variant="outline"
        />
      )}

      <div className="border-t pt-4">
        <ActionCard
          icon={<Trash2 className="h-5 w-5 text-destructive" />}
          title="Delete User"
          description="Permanently delete this user. This action cannot be undone."
          buttonLabel="Delete User"
          onClick={() => onAction('delete')}
          disabled={isSubmitting}
          variant="destructive"
        />
      </div>
    </div>
  );
}

interface ActionCardProps {
  readonly icon: React.ReactNode;
  readonly title: string;
  readonly description: string;
  readonly buttonLabel: string;
  readonly onClick: () => void;
  readonly disabled?: boolean;
  readonly variant?: 'default' | 'outline' | 'destructive';
}

function ActionCard({
  icon,
  title,
  description,
  buttonLabel,
  onClick,
  disabled,
  variant = 'default',
}: ActionCardProps) {
  return (
    <div className="flex items-center justify-between rounded-lg border p-4">
      <div className="flex items-start gap-3">
        {icon}
        <div>
          <h4 className="font-medium">{title}</h4>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
      </div>
      <Button
        variant={variant}
        size="sm"
        onClick={onClick}
        disabled={disabled}
      >
        {buttonLabel}
      </Button>
    </div>
  );
}

export function Timestamps({ user }: { readonly user: User }) {
  return (
    <div className="flex gap-4 border-t pt-4 text-xs text-muted-foreground">
      <span>Created: {new Date(user.createdAt).toLocaleDateString()}</span>
      {user.lastLoginAt && (
        <span>Last login: {new Date(user.lastLoginAt).toLocaleDateString()}</span>
      )}
    </div>
  );
}
